import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { LinearProgress } from '@mui/material';

import physicalStockService from '../../services/physicalStockService';
import productService from '../../services/productService';
import locationService from '../../services/locationService';

// Styled Components (matching POSKeyboard.js)
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '8px 12px',
  border: '1px solid #e0e0e0',
  fontSize: '15px',
  '&.focused': {
    backgroundColor: '#e3f2fd',
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 12px',
  border: '1px solid #bdc3c7',
  backgroundColor: '#154c79',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '15px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
}));

const FunctionKey = styled(Button)(({ theme }) => ({
  minWidth: '100px',
  padding: '8px 12px',
  backgroundColor: '#ecf0f1',
  color: '#2c3e50',
  fontSize: '13px',
  fontWeight: '600',
  textTransform: 'none',
  border: '1px solid #bdc3c7',
  '&:hover': {
    backgroundColor: '#bdc3c7',
  },
  '& .key': {
    fontWeight: 'bold',
    color: '#3498db',
    fontSize: '14px',
    marginRight: '4px',
  },
}));

const PhysicalStockEntry = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Refs for focus management
  const itemCodeRefs = useRef([]);
  const physicalStockRefs = useRef([]);
  const reasonRefs = useRef([]);

  // State
  const [items, setItems] = useState([
    { id: 1, itemCode: '', productName: '', systemStock: 0, physicalStock: '', difference: 0, reason: '', product: null }
  ]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [productOptions, setProductOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [locationStockMap, setLocationStockMap] = useState({});

  const REASONS_GAIN = [
    'Free quantity received',
    'Stock adjustment(excess found)',
    'Audit adjustment ',
    'Local purchase',
    'Branch transfer in',
    'Other'
  ];
  const REASONS_LOSS = [
    'Sample/Free issue ',
    'Theft/loss',
    'Warranty replacement out',
    'Expired Goods',
    'Transport Damage',
    'Damage/Breakage',
    'Other'
  ];

  // Initial Load
  useEffect(() => {
    fetchLocations();
    loadAllProducts();
    setReferenceNumber(`PS-${Date.now()}`);
    setTimeout(() => {
      if (itemCodeRefs.current[0]) {
        itemCodeRefs.current[0].focus();
      }
    }, 200);
  }, []);

  // Keyboard Shortcuts
  useEffect(() => {
    const handler = (e) => {
      // F3: Focus First Item Code
      if (e.key === 'F3') {
        e.preventDefault();
        if (itemCodeRefs.current[0]) {
          itemCodeRefs.current[0].focus();
        }
        return;
      }

      // F7: Clear All
      if (e.key === 'F7') {
        e.preventDefault();
        handleClearAll();
        return;
      }

      // Ctrl+S: Save
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
        return;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, selectedLocation, notes]); // Dependencies for save handler

  const fetchLocations = async () => {
    try {
      // Load using direct axiosInstance like PhysicalStockUpdate for consistency
      const { default: axiosInstance } = await import('../../utils/axios');
      const res = await axiosInstance.get('/locations', { params: { page: 0, size: 1000, sort: 'name', direction: 'ASC' } });
      const list = res.data?.content || res.data || [];
      // Deduplicate by name (case-insensitive)
      const unique = [];
      const seen = new Set();
      list.forEach(l => {
        const n = (l?.name || '').toLowerCase().trim();
        if (n && !seen.has(n)) {
          seen.add(n);
          unique.push(l);
        }
      });
      setLocations(unique);

      // Set 'Main Location' as default if it exists
      if (unique.length > 0) {
        const mainLoc = unique.find(l =>
          l.name?.toLowerCase().includes('main location') ||
          l.name?.toLowerCase() === 'main location'
        );
        if (mainLoc) {
          setSelectedLocation(mainLoc.id);
        }
      }
    } catch (error) {
      console.error("Error fetching locations", error);
      setLocations([]);
    }
  };

  useEffect(() => {
    const loadLocationStocks = async () => {
      if (!selectedLocation) {
        setLocationStockMap({});
        return;
      }
      try {
        const { default: stockTransferService } = await import('../../services/stockTransferService');
        const res = await stockTransferService.getLocationProducts(selectedLocation);
        const list = Array.isArray(res) ? res : (res?.content || []);
        const map = {};
        list.forEach(item => {
          const pid = item.productId || item.product?.id || item.id;
          const qty = item.quantity ?? item.stock ?? item.availableQuantity ?? 0;
          if (pid) map[pid] = qty;
        });
        setLocationStockMap(map);
      } catch (e) {
        console.error('Failed to load location products', e);
        setLocationStockMap({});
      }
    };
    loadLocationStocks();
  }, [selectedLocation]);

  const loadAllProducts = async () => {
    try {
      // Fetch all products for autocomplete
      // Depending on data size, this might need optimization (search on type)
      // For now, fetching a reasonable batch or relying on search API on type
      const res = await productService.getAllProducts(0, 1000, 'name', 'asc');
      const list = Array.isArray(res) ? res : (res?.content || []);
      const activeProducts = list.filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
      setAllProducts(activeProducts);
    } catch (e) {
      console.error('Failed to load products', e);
    }
  };

  // Product Search & Selection
  const handleItemCodeInputChange = (rowIndex, value) => {
    const newItems = [...items];
    const v = typeof value === 'string' ? value : '';
    newItems[rowIndex].itemCode = v;
    setItems(newItems);

    // Filter for autocomplete
    if (v && v.length >= 1) {
      const lower = v.toLowerCase();
      const filtered = allProducts.filter(p =>
        (p.name || '').toLowerCase().includes(lower) ||
        (p.sku && p.sku.toLowerCase().includes(lower)) ||
        (p.barcode && p.barcode.toLowerCase().includes(lower))
      ).slice(0, 10);

      const newOptions = { ...productOptions };
      newOptions[rowIndex] = filtered;
      setProductOptions(newOptions);
    } else {
      const newOptions = { ...productOptions };
      newOptions[rowIndex] = [];
      setProductOptions(newOptions);
    }
  };

  const searchProduct = async (code, rowIndex) => {
    if (!code || code.trim() === '') return;

    try {
      setSearchingProduct(true);
      let product = allProducts.find(p =>
        (p.sku && p.sku.toLowerCase() === code.toLowerCase()) ||
        (p.barcode && p.barcode.toLowerCase() === code.toLowerCase()) ||
        (p.code && p.code.toLowerCase() === code.toLowerCase())
      );

      if (!product) {
        // Fallback to API search if not found in preloaded list
        const res = await productService.searchProducts(code);
        const list = Array.isArray(res) ? res : (res?.content || []);
        if (list.length > 0) product = list[0];
      }

      if (product) {
        addProductToRow(rowIndex, product);
      } else {
        alert('Product not found in inventory');
      }
    } catch (e) {
      console.error('Product search failed', e);
      alert('Failed to search product. Please try again.');
    } finally {
      setSearchingProduct(false);
    }
  };

  const addProductToRow = (rowIndex, product) => {
    // Check if product already exists in another row
    const existingIndex = items.findIndex((item, idx) => item.product && item.product.id === product.id && idx !== rowIndex);
    if (existingIndex !== -1) {
      alert('Product already added to the list');
      // Focus the existing row's physical stock
      if (physicalStockRefs.current[existingIndex]) {
        physicalStockRefs.current[existingIndex].focus();
        physicalStockRefs.current[existingIndex].select();
      }
      // Clear current row if it was just typing
      if (!items[rowIndex].product) {
        const newItems = [...items];
        newItems[rowIndex].itemCode = '';
        setItems(newItems);
      }
      return;
    }

    const newItems = [...items];
    const locationQty = locationStockMap[product.id];
    newItems[rowIndex] = {
      ...newItems[rowIndex],
      itemCode: product.sku || product.barcode || product.name,
      productName: product.name,
      systemStock: (locationQty ?? product.quantity ?? 0),
      physicalStock: (locationQty ?? product.quantity ?? 0),
      difference: 0,
      reason: '',
      product: product
    };

    setItems(newItems);

    // Focus Physical Stock field of the current row
    setTimeout(() => {
      if (physicalStockRefs.current[rowIndex]) {
        physicalStockRefs.current[rowIndex].focus();
        physicalStockRefs.current[rowIndex].select();
      }
    }, 100);
  };

  const handleProductSelect = (rowIndex, product) => {
    if (product) {
      addProductToRow(rowIndex, product);
    }
  };

  const handleItemCodeKeyDown = (e, rowIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Trigger search if not selected via autocomplete (which handles its own enter)
      const hasOptions = productOptions[rowIndex] && productOptions[rowIndex].length > 0;
      if (items[rowIndex].itemCode && !items[rowIndex].product && hasOptions) {
        const first = productOptions[rowIndex][0];
        if (first) {
          handleProductSelect(rowIndex, first);
        }
      } else if (items[rowIndex].itemCode && !items[rowIndex].product && !hasOptions && !searchingProduct) {
        searchProduct(items[rowIndex].itemCode, rowIndex);
      } else if (items[rowIndex].product) {
        // If already has product, move to next field
        if (physicalStockRefs.current[rowIndex]) {
          physicalStockRefs.current[rowIndex].focus();
        }
      }
    }
  };

  // Stock & Reason Handling
  const handlePhysicalStockChange = (rowIndex, value) => {
    const newItems = [...items];
    const physicalStock = value === '' ? '' : parseFloat(value);
    newItems[rowIndex].physicalStock = physicalStock;

    if (newItems[rowIndex].product && physicalStock !== '') {
      const diff = physicalStock - newItems[rowIndex].systemStock;
      newItems[rowIndex].difference = parseFloat(diff.toFixed(2));
      // Auto-clear reason if difference is 0
      if (diff === 0) newItems[rowIndex].reason = '';
    }

    setItems(newItems);
  };

  const handlePhysicalStockKeyDown = (e, rowIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (items[rowIndex].difference !== 0) {
        if (reasonRefs.current[rowIndex]) {
          reasonRefs.current[rowIndex].focus();
        }
      } else {
        const isLastRow = rowIndex === items.length - 1;
        if (isLastRow) {
          const newItems = [...items, { id: items.length + 2, itemCode: '', productName: '', systemStock: 0, physicalStock: '', difference: 0, reason: '', product: null }];
          setItems(newItems);
          setTimeout(() => {
            if (itemCodeRefs.current[rowIndex + 1]) {
              itemCodeRefs.current[rowIndex + 1].focus();
            }
          }, 100);
        } else {
          const nextRowIndex = rowIndex + 1;
          if (itemCodeRefs.current[nextRowIndex]) {
            itemCodeRefs.current[nextRowIndex].focus();
          }
        }
      }
    }
  };

  const handleReasonChange = (rowIndex, value) => {
    const newItems = [...items];
    newItems[rowIndex].reason = value;
    setItems(newItems);
    // After selecting a reason for a non-zero difference, move to next row and create one if needed
    if (items[rowIndex].difference !== 0) {
      const isLastRow = rowIndex === items.length - 1;
      if (isLastRow) {
        const empty = { id: items.length + 2, itemCode: '', productName: '', systemStock: 0, physicalStock: '', difference: 0, reason: '', product: null };
        setItems(prev => [...prev, empty]);
        setTimeout(() => {
          if (itemCodeRefs.current[rowIndex + 1]) itemCodeRefs.current[rowIndex + 1].focus();
        }, 100);
      } else {
        setTimeout(() => {
          if (itemCodeRefs.current[rowIndex + 1]) itemCodeRefs.current[rowIndex + 1].focus();
        }, 100);
      }
    }
  };

  const handleDeleteRow = (rowIndex) => {
    if (items.length === 1) {
      handleClearAll();
      return;
    }
    const newItems = items.filter((_, idx) => idx !== rowIndex);
    setItems(newItems);
  };

  const handleClearAll = () => {
    setItems([{ id: 1, itemCode: '', productName: '', systemStock: 0, physicalStock: '', difference: 0, reason: '', product: null }]);
    setNotes('');
    setSelectedLocation('');
    setReferenceNumber(`PS-${Date.now()}`);
    // Full refresh as requested
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  const handleSave = async () => {
    const validItems = items.filter(item => item.product);

    if (validItems.length === 0) {
      enqueueSnackbar('No items to save', { variant: 'warning' });
      return;
    }

    // Validation
    const missingReason = validItems.find(item => item.difference !== 0 && !item.reason);
    if (missingReason) {
      enqueueSnackbar(`Please provide a reason for ${missingReason.productName} (Difference: ${missingReason.difference})`, { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        referenceNumber,
        locationId: selectedLocation || null,
        notes,
        details: validItems.map(item => ({
          productId: item.product.id,
          systemStock: item.systemStock,
          physicalStock: Number(item.physicalStock),
          difference: item.difference,
          reason: item.reason
        }))
      };

      console.log('Saving physical stock payload', payload);
      await physicalStockService.create(payload);
      enqueueSnackbar('Physical stock updated successfully', { variant: 'success' });
      navigate('/inventory/view-physical-stock-entry');
    } catch (error) {
      console.error('Error saving physical stock', error);
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message || error.message;
      if (status === 403) {
        enqueueSnackbar(serverMessage || 'Access denied', { variant: 'error' });
      } else if (status === 500) {
        enqueueSnackbar(serverMessage || 'Server error', { variant: 'error' });
      } else {
        enqueueSnackbar(serverMessage || 'Failed to update physical stock', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculations
  const totalItems = items.filter(i => i.product).length;
  const totalDifference = items.reduce((sum, i) => sum + (parseFloat(i.difference) || 0), 0);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f1f1f1', fontFamily: '"Roboto", sans-serif', overflow: 'hidden' }}>

      {/* 1. Header Section */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          backgroundColor: '#154c79',
          color: 'white',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 1100,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
          PHYSICAL STOCK ENTRY
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Ref No: <span style={{ fontWeight: 'normal' }}>{referenceNumber}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(-1)}
              sx={{ backgroundColor: 'white', color: '#154c79', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#f5f5f5' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>←</span>}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearAll}
              sx={{ borderColor: '#ef5350', color: '#ef5350', backgroundColor: 'white', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#ffebee', borderColor: '#ef5350' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>⟲</span>}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 2. Top Info Section */}
      <Paper elevation={1} sx={{ p: 1.5, m: 1, borderRadius: 1 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Left Side: Inputs */}
          <Grid item xs={12} md={9}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={4}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Warehouse / Location</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    displayEmpty
                    sx={{ backgroundColor: 'white', fontSize: '15px' }}
                    renderValue={(selected) => {
                      if (!selected) return <em>Select Location</em>;
                      const loc = locations.find(l => l.id === selected);
                      return loc ? loc.name : selected;
                    }}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Notes</Typography>
                <TextField
                  fullWidth
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{ backgroundColor: 'white', '& .MuiInputBase-input': { fontSize: '15px' } }}
                  placeholder="Additional identification notes..."
                />
              </Grid>
              {/* Stats Chips */}
              <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Items: ${totalItems}`}
                  sx={{ backgroundColor: '#e0f2f1', color: '#00695c', fontWeight: 'bold', border: '1px solid #b2dfdb', height: '40px', fontSize: '16px', flex: 1 }}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Right Side: Net Difference (Big Total) */}
          <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: totalDifference < 0 ? '#e74c3c' : totalDifference > 0 ? '#27ae60' : '#7f8c8d', lineHeight: 1, fontSize: '3.5rem' }}>
              {totalDifference}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#7f8c8d' }}>NET DIFFERENCE</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 3. Main Table Section - Scrollable */}
      <Box sx={{ flexGrow: 1, px: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableContainer component={Paper} elevation={1} sx={{ flexGrow: 1, borderRadius: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableHeadCell sx={{ width: '50px' }}>S.No</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '200px' }}>Item Code / Barcode</StyledTableHeadCell>
                <StyledTableHeadCell>Product Name</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '100px' }}>System Stock</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '120px' }}>Physical Stock</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '100px' }}>Difference</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '250px' }}>Reason</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ width: '60px' }}>Action</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const isSearching = !item.product;
                const rowBg = isSearching ? '#fffde7' : index % 2 === 0 ? '#f8f9fb' : 'white';
                return (
                  <TableRow key={item.id} sx={{ backgroundColor: rowBg }}>
                    <StyledTableCell align="center">{index + 1}</StyledTableCell>
                    <StyledTableCell sx={{ position: 'relative', overflow: 'visible', zIndex: 1 }}>
                      <Autocomplete
                        freeSolo
                        options={Array.isArray(productOptions[index]) ? productOptions[index] : []}
                        value={items[index].product || null}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          if (!option) return '';
                          return option.sku ? `${option.name} (${option.sku})` : option.name;
                        }}
                        inputValue={item.itemCode ?? ''}
                        onInputChange={(e, value, reason) => {
                          if (reason === 'clear') {
                            const newItems = [...items];
                            newItems[index] = { ...newItems[index], itemCode: '', productName: '', systemStock: 0, physicalStock: '', difference: 0, reason: '', product: null };
                            setItems(newItems);
                          } else {
                            handleItemCodeInputChange(index, value);
                          }
                        }}
                        onChange={(e, value) => handleProductSelect(index, value)}
                        autoHighlight
                        noOptionsText="No product found"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Scan/Type Code"
                            variant="standard"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                            inputRef={el => itemCodeRefs.current[index] = el}
                            onKeyDown={(e) => handleItemCodeKeyDown(e, index)}
                            sx={{ '& input': { fontSize: '15px' } }}
                          />
                        )}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{item.productName}</StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.systemStock}</Typography>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        value={item.physicalStock}
                        onChange={(e) => handlePhysicalStockChange(index, e.target.value)}
                        onKeyDown={(e) => handlePhysicalStockKeyDown(e, index)}
                        type="number"
                        variant="standard"
                        fullWidth
                        InputProps={{ disableUnderline: true, inputProps: { style: { textAlign: 'right', fontWeight: 'bold', fontSize: '15px' } } }}
                        disabled={!item.product}
                        inputRef={el => physicalStockRefs.current[index] = el}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{
                      color: item.difference < 0 ? '#e74c3c' : item.difference > 0 ? '#27ae60' : 'text.primary',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}>
                      {item.difference > 0 ? `+${item.difference}` : item.difference}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Select
                        value={item.reason}
                        onChange={(e) => handleReasonChange(index, e.target.value)}
                        displayEmpty
                        fullWidth
                        variant="standard"
                        disableUnderline
                        disabled={item.difference === 0}
                        sx={{ fontSize: '14px' }}
                        inputRef={el => reasonRefs.current[index] = el}
                      >
                        <MenuItem value="" disabled>Select Reason</MenuItem>
                        {(item.difference > 0 ? REASONS_GAIN : REASONS_LOSS).map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton size="small" onClick={() => handleDeleteRow(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 4. Footer Section */}
      <Paper elevation={2} sx={{ p: 1, m: 1, mt: 0, borderRadius: 1 }}>
        <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: '#27ae60 !important',
                color: 'white !important',
                opacity: 1,
                fontWeight: 'bold',
                fontSize: '18px',
                textTransform: 'none',
                py: 1.5,
                '&:hover': { backgroundColor: '#219150 !important' },
                '&.Mui-disabled': { backgroundColor: '#27ae60 !important', color: 'white !important', opacity: 0.7 }
              }}
              startIcon={<SaveIcon sx={{ fontSize: '24px', marginRight: '8px' }} />}
            >
              Save Entry
            </Button>
          </Grid>
        </Grid>
      </Paper>

    </Box>
  );
};

export default PhysicalStockEntry;
